import {
    memo,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    type ReactNode,
    type RefObject
} from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { OverlayScrollArea } from '@odo/shared-ui';
import type { IteroClinic } from '../../../../services/iteroConnection';
import { practiceNames } from './iteroConnectionCopy';
import {
    CLINIC_SEARCH_PLACEHOLDER,
    clinicCountText,
    filterClinics,
    noMatchText,
    sortClinics
} from './iteroClinicSearch';

interface IteroClinicPickerProps {
    clinics: IteroClinic[];
    selectedId: number | null;
    disabled: boolean;
    onSelect: (clinicId: number) => void;
    /** Loading, error or empty state shown centred in the list area; disables the search. */
    statusContent: ReactNode | null;
}

interface IteroClinicOptionProps {
    clinic: IteroClinic;
    groupName: string;
    checked: boolean;
    disabled: boolean;
    onSelect: (clinicId: number) => void;
}

const SCROLL_TRACK_INSET_PX = 4;

const IteroClinicOption = memo(function IteroClinicOption({
    clinic,
    groupName,
    checked,
    disabled,
    onSelect
}: IteroClinicOptionProps) {
    const rowRef = useRef<HTMLLabelElement>(null);
    // The browser scrolls only the radio into view, after onFocus; a frame later show the whole row.
    const revealRow = () =>
        window.requestAnimationFrame(() => rowRef.current?.scrollIntoView({ block: 'nearest' }));

    return (
        <label
            ref={rowRef}
            className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border transition-colors ${
                checked ? 'border-gray-700 bg-gray-50' : 'border-gray-200 hover:bg-gray-50'
            } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
        >
            <input
                type="radio"
                name={groupName}
                checked={checked}
                disabled={disabled}
                onChange={() => onSelect(clinic.clinicId)}
                onFocus={revealRow}
                className="mt-0.5 h-4 w-4 border-gray-300"
            />
            <span className="flex flex-col gap-0.5 min-w-0 text-sm text-gray-700">
                <span className="font-semibold text-gray-800">
                    {clinic.name}
                    {clinic.isCurrent && (
                        <span className="ml-1 font-normal text-gray-500">(current)</span>
                    )}
                </span>
                <span className="text-xs text-gray-500 font-mono">iTero ID {clinic.clinicId}</span>
                {clinic.linkedPractices.length > 0 && (
                    <span className="text-xs text-gray-500">
                        Linked to {practiceNames(clinic.linkedPractices)}
                    </span>
                )}
            </span>
        </label>
    );
});

interface ClinicSearchFieldProps {
    inputRef: RefObject<HTMLInputElement>;
    value: string;
    disabled: boolean;
    onChange: (value: string) => void;
}

function ClinicSearchField({ inputRef, value, disabled, onChange }: ClinicSearchFieldProps) {
    const inputId = useId();
    // Mounted disabled while clinics load, so autoFocus alone would never land.
    useEffect(() => {
        if (!disabled) inputRef.current?.focus();
    }, [disabled, inputRef]);

    return (
        <span
            className={`h-10 border border-gray-300 rounded-lg px-3 flex items-center gap-2 shrink-0 ${
                disabled ? 'bg-gray-50' : ''
            }`}
        >
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 shrink-0" aria-hidden="true" />
            <label htmlFor={inputId} className="sr-only">
                Search iTero clinics
            </label>
            <input
                ref={inputRef}
                id={inputId}
                type="search"
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                placeholder={CLINIC_SEARCH_PLACEHOLDER}
                autoComplete="off"
                className="flex-1 min-w-0 text-sm text-gray-800 placeholder-gray-400 outline-none bg-transparent disabled:cursor-not-allowed"
            />
        </span>
    );
}

function NoClinicMatch({ query, onClear }: { query: string; onClear: () => void }) {
    return (
        <p className="text-sm text-gray-600 text-center">
            {noMatchText(query)}{' '}
            <button
                type="button"
                onClick={onClear}
                className="font-semibold text-blue-600 hover:underline"
            >
                Clear search
            </button>
        </p>
    );
}

interface ClinicRadioListProps {
    clinics: IteroClinic[];
    selectedId: number | null;
    disabled: boolean;
    onSelect: (clinicId: number) => void;
}

function ClinicRadioList({ clinics, selectedId, disabled, onSelect }: ClinicRadioListProps) {
    const groupName = useId();
    return (
        <OverlayScrollArea
            className="h-full"
            contentClassName="pr-3"
            trackInsetPx={SCROLL_TRACK_INSET_PX}
        >
            <div role="radiogroup" aria-label="iTero clinics" className="flex flex-col gap-2">
                {clinics.map((clinic) => (
                    <IteroClinicOption
                        key={clinic.clinicId}
                        clinic={clinic}
                        groupName={groupName}
                        checked={clinic.clinicId === selectedId}
                        disabled={disabled}
                        onSelect={onSelect}
                    />
                ))}
            </div>
        </OverlayScrollArea>
    );
}

function CenteredStatus({ children }: { children: ReactNode }) {
    return <div className="h-full flex items-center justify-center px-4">{children}</div>;
}

export function IteroClinicPicker({
    clinics,
    selectedId,
    disabled,
    onSelect,
    statusContent
}: IteroClinicPickerProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState('');
    const sorted = useMemo(() => sortClinics(clinics), [clinics]);
    const shown = useMemo(() => filterClinics(sorted, query), [sorted, query]);
    const hasStatus = statusContent !== null;

    const clearSearch = () => {
        setQuery('');
        inputRef.current?.focus();
    };

    let listArea: ReactNode;
    if (hasStatus) {
        listArea = <CenteredStatus>{statusContent}</CenteredStatus>;
    } else if (shown.length === 0) {
        listArea = (
            <CenteredStatus>
                <NoClinicMatch query={query} onClear={clearSearch} />
            </CenteredStatus>
        );
    } else {
        listArea = (
            <ClinicRadioList
                clinics={shown}
                selectedId={selectedId}
                disabled={disabled}
                onSelect={onSelect}
            />
        );
    }

    return (
        <div className="flex flex-col gap-2 flex-1 min-h-0">
            <ClinicSearchField
                inputRef={inputRef}
                value={query}
                disabled={hasStatus}
                onChange={setQuery}
            />
            <p className="h-4 text-xs text-gray-500 shrink-0" aria-live="polite">
                {hasStatus ? '' : clinicCountText(shown.length, clinics.length)}
            </p>
            <div className="flex-1 min-h-0">{listArea}</div>
        </div>
    );
}
